<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { eqsApi } from '@/api/eqs';
import { notify } from '@/services/notice';
import { errorMessage } from '@/utils/format';
import { confirmAction } from '@/services/confirm';

interface SimilarIssue {
  id: number; code: string | null; title: string; status: string;
  projectNo: string | null; category: string | null;
  rootCause: string | null; countermeasure: string | null; reason: string;
}

interface AssistItem {
  fields: Record<string, string>;
  unmatchedOwner: string | null;
  evidence: string;
  similar: SimilarIssue[];
}

const props = defineProps<{
  open: boolean;
  projectId: string | number;
}>();

const emit = defineEmits<{
  close: [];
  /** 单条：填进新建表单里让人复核 */
  adopt: [fields: Record<string, string>];
  /** 批量创建完成，父级刷新列表 */
  created: [count: number];
}>();

const text = ref('');
/** 上传的截图 / 表格；与文字二选一或并用 */
const file = ref<File | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
/** 从文件里读出来的原文，摆给人核对——AI 读错了得能一眼看出来 */
const sourceText = ref('');
const sourceNote = ref('');
const parsing = ref(false);
const creating = ref(false);
const items = ref<AssistItem[]>([]);
const checked = ref<Set<number>>(new Set());
const confidence = ref<'high' | 'low'>('low');
const parsed = ref(false);

const checkedCount = computed(() => checked.value.size);

const FIELD_LABELS: Readonly<Record<string, string>> = {
  title: '标题', description: '描述', category: '分类', occurrenceStage: '发生阶段',
  foundBy: '发现人', location: '地点/工位', productCategory: '产品类别',
  customerName: '客户', partNumber: '零件号', partName: '零件名', quantity: '数量',
  priority: '优先级', ownerName: '责任人', dueAt: '要求完成',
};

function reset(): void {
  text.value = '';
  file.value = null;
  if (fileInput.value) fileInput.value.value = '';
  sourceText.value = '';
  sourceNote.value = '';
  items.value = [];
  checked.value = new Set();
  parsed.value = false;
}

function onFileChange(event: Event): void {
  file.value = (event.target as HTMLInputElement).files?.[0] ?? null;
}

/** 有文件就能解析；纯文字仍要求写清楚（太短的一句话抽不出东西） */
const canParse = computed(() => Boolean(file.value) || text.value.trim().length >= 8);

watch(() => props.open, (open) => { if (open) reset(); });

/*
  自动解析（1006 Tony：「把问题建议改成默认动作」）。
  30 天里这个功能只被点了 4 次——人贴完文字还要再找「解析」按钮。现在贴完停 1.2 秒就自动跑一次；
  选了文件也立刻跑。只在还没解析过时自动触发，解析完人改了文字想重来，仍点按钮——
  免得每改一个字就打一次 AI。
*/
const AUTO_PARSE_DELAY_MS = 1200;
const AUTO_PARSE_MIN_CHARS = 20;
let autoParseTimer: number | null = null;
function cancelAutoParse(): void {
  if (autoParseTimer !== null) {
    window.clearTimeout(autoParseTimer);
    autoParseTimer = null;
  }
}
watch(text, (value) => {
  cancelAutoParse();
  if (parsed.value || parsing.value || file.value) return;
  if (value.trim().length < AUTO_PARSE_MIN_CHARS) return;
  autoParseTimer = window.setTimeout(() => {
    autoParseTimer = null;
    if (!parsed.value && !parsing.value && canParse.value) void parse();
  }, AUTO_PARSE_DELAY_MS);
});
watch(file, (value) => {
  cancelAutoParse();
  if (value && !parsed.value && !parsing.value) void parse();
});
watch(() => props.open, (open) => { if (!open) cancelAutoParse(); });

async function parse(): Promise<void> {
  parsing.value = true;
  try {
    const payload = await eqsApi.assistIssueIntake(props.projectId, text.value, file.value) as {
      items: AssistItem[]; confidence: 'high' | 'low';
      sourceText?: string; sourceNote?: string;
    };
    items.value = payload.items ?? [];
    confidence.value = payload.confidence;
    sourceText.value = payload.sourceText ?? '';
    sourceNote.value = payload.sourceNote ?? '';
    // 默认全选：这是提议不是落库，勾选框就是确认动作
    checked.value = new Set(items.value.map((_, index) => index));
    parsed.value = true;
    if (!items.value.length) {
      notify('error', '没有识别出问题', '文本里可能没有可立单的问题；决议和日程安排不会被抽取。');
    }
  } catch (caught) {
    notify('error', 'AI 解析失败', errorMessage(caught));
  } finally {
    parsing.value = false;
  }
}

function toggle(index: number): void {
  const next = new Set(checked.value);
  if (next.has(index)) next.delete(index);
  else next.add(index);
  checked.value = next;
}

function fieldEntries(item: AssistItem): Array<[string, string]> {
  return Object.entries(item.fields).filter(([key]) => key !== 'description');
}

/** 与 ProjectSectionView.saveDetail 同一套拆分口径：核心列进顶层，其余进 normalized */
function buildPayload(fields: Record<string, string>): Record<string, unknown> {
  const coreKeys = new Set(['code', 'title', 'priority', 'ownerName', 'dueAt']);
  const payload: Record<string, unknown> = {};
  const normalized: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(fields)) {
    const value = raw.trim();
    if (!value) continue;
    if (coreKeys.has(key)) payload[key] = value;
    else normalized[key] = key === 'quantity' ? Number(value) : value;
  }
  if (Object.keys(normalized).length) payload.normalized = normalized;
  if (typeof payload.dueAt === 'string' && payload.dueAt) {
    payload.dueAt = new Date(`${payload.dueAt}T23:59:59+08:00`).toISOString();
  }
  return payload;
}

async function createChecked(): Promise<void> {
  const targets = items.value.filter((_, index) => checked.value.has(index));
  if (!targets.length) return;
  const ungraded = targets.filter((item) => !String(item.fields.severityGrade ?? '').trim());
  if (ungraded.length) {
    notify('error', `${ungraded.length} 条还没选等级`, '新建问题必须定级：等级决定这条问题起步在谁手上。');
    return;
  }
  if (!await confirmAction({ title: `确认创建 ${targets.length} 条问题？每条都会进入问题清单并记入审计。` })) return;
  creating.value = true;
  let done = 0;
  const failed: string[] = [];
  // 逐条创建：中途失败时已建的保留、失败的报出来，不搞「全有或全无」——
  // 纪要里 5 条建成 4 条，比因为第 5 条报错整批丢弃更符合实际
  for (const item of targets) {
    try {
      await eqsApi.createProjectResource('issues', 'issues', props.projectId, buildPayload(item.fields));
      done += 1;
    } catch (caught) {
      failed.push(`「${item.fields.title}」：${errorMessage(caught)}`);
    }
  }
  creating.value = false;
  if (done) {
    notify('success', `已创建 ${done} 条问题`, failed.length ? `另有 ${failed.length} 条失败。` : '全部写入问题清单。');
    emit('created', done);
  }
  if (failed.length) notify('error', '部分创建失败', failed.slice(0, 3).join('\n'));
  if (!failed.length) emit('close');
}
</script>

<template>
  <div v-if="open" class="assist-mask" @click.self="!parsing && !creating && emit('close')">
    <section class="assist-dialog" role="dialog" aria-modal="true" aria-label="AI 立单与纪要导入">
      <header>
        <div>
          <span>AI ISSUE INTAKE</span>
          <h3>智能立单 / 会议纪要导入</h3>
          <p>贴一条客诉出一条；贴整份例会纪要出多条。也可以直接传截图或 Excel。AI 只提议，创建前逐条勾选确认。</p>
        </div>
        <button class="inline-button" type="button" :disabled="parsing || creating" @click="emit('close')">关闭</button>
      </header>

      <textarea
        v-model="text"
        rows="6"
        maxlength="20000"
        placeholder="粘贴问题描述或会议纪要原文…（决议、日程不会被抽取成问题）"
        aria-label="问题描述或会议纪要"
      ></textarea>
      <label class="assist-file">
        <span>或上传截图 / 表格</span>
        <input
          ref="fileInput"
          type="file"
          accept=".png,.jpg,.jpeg,.webp,.bmp,.xlsx,.txt,.md,.csv"
          @change="onFileChange"
        >
        <small>微信聊天截图、缺陷照片、客诉 Excel 都行；Word / PDF 请把正文复制到上面的输入框。</small>
      </label>

      <div class="assist-actions">
        <button class="button button-primary" type="button" :disabled="parsing || !canParse" @click="parse">
          {{ parsing ? 'AI 解析中…' : parsed ? '重新解析' : '解析' }}
        </button>
        <small v-if="!parsed && !parsing" class="assist-auto-hint">贴完停一下会自动解析；选了文件也会自动开始。</small>
        <small v-if="parsed && confidence === 'low'" class="assist-low">AI 对这次解析把握不大，请逐条核对</small>
      </div>

      <!-- 从文件读到什么，原样摆出来：识别错了要能当场发现，而不是等问题建完才察觉 -->
      <details v-if="sourceText" class="assist-source">
        <summary>{{ sourceNote || '文件读取结果' }}（点开核对）</summary>
        <pre>{{ sourceText }}</pre>
      </details>

      <div v-if="items.length" class="assist-items">
        <article v-for="(item, index) in items" :key="index" :class="{ off: !checked.has(index) }">
          <label class="assist-pick">
            <input type="checkbox" :checked="checked.has(index)" @change="toggle(index)">
            <b>{{ item.fields.title }}</b>
            <!-- 0911 起新建必须定级：AI 提不出等级，得人选 -->
            <select v-model="item.fields.severityGrade" class="assist-grade" aria-label="问题等级" :class="{ missing: checked.has(index) && !item.fields.severityGrade }" @click.stop>
              <option value="">等级（必选）</option>
              <option value="A-严重">A-严重</option>
              <option value="B-重要">B-重要</option>
              <option value="C-一般">C-一般</option>
              <option value="D-轻微">D-轻微</option>
            </select>
          </label>
          <p v-if="item.fields.description" class="assist-desc">{{ item.fields.description }}</p>
          <dl class="assist-fields">
            <template v-for="[key, value] in fieldEntries(item)" :key="key">
              <template v-if="key !== 'title'">
                <dt>{{ FIELD_LABELS[key] ?? key }}</dt><dd>{{ value }}</dd>
              </template>
            </template>
          </dl>
          <p v-if="item.unmatchedOwner" class="assist-warn">
            纪要点名「{{ item.unmatchedOwner }}」，但此人不在项目团队里——创建后请手动指定责任人。
          </p>
          <p v-if="item.evidence" class="assist-evidence">原文：{{ item.evidence }}</p>
          <div v-if="item.similar.length" class="assist-similar">
            <b>以前类似的问题：</b>
            <div v-for="hit in item.similar" :key="hit.id" class="assist-case">
              <span>
                {{ hit.projectNo ? `${hit.projectNo} · ` : '' }}{{ hit.title }}
                <em>（{{ hit.reason }}）</em>
              </span>
              <small v-if="hit.rootCause">根因：{{ hit.rootCause }}</small>
              <small v-if="hit.countermeasure">对策：{{ hit.countermeasure }}</small>
            </div>
          </div>
          <button
            v-if="items.length === 1"
            class="inline-button"
            type="button"
            @click="emit('adopt', item.fields)"
          >填入表单逐项复核 →</button>
        </article>
      </div>

      <footer v-if="items.length">
        <button class="button button-primary" type="button" :disabled="creating || !checkedCount" @click="createChecked">
          {{ creating ? '创建中…' : `确认创建 ${checkedCount} 条` }}
        </button>
        <small>创建后可在问题清单里继续维护责任人与进展。</small>
      </footer>
    </section>
  </div>
</template>

<style scoped>
.assist-grade { margin-left: auto; font-size: 12px; padding: 2px 6px; border: 1px solid var(--line-strong); border-radius: 3px; }
.assist-grade.missing { border-color: var(--risk); }

.assist-mask { position: fixed; inset: 0; z-index: 60; display: grid; place-items: center; padding: 20px; background: rgba(16, 39, 56, .4); }
.assist-dialog { display: grid; gap: 12px; width: min(760px, 100%); max-height: 90vh; overflow-y: auto; padding: 18px 20px; background: var(--white); box-shadow: 0 10px 40px rgba(16, 39, 56, .25); }
.assist-dialog > header { display: flex; justify-content: space-between; gap: 14px; }
.assist-dialog header span { color: var(--muted); font: 700 11px var(--display); letter-spacing: .08em; }
.assist-dialog header h3 { margin: 2px 0 4px; font-size: 17px; }
.assist-dialog header p { margin: 0; color: var(--muted); font-size: 12px; line-height: 1.7; }
.assist-dialog textarea { padding: 9px 11px; border: 1px solid var(--line); font: inherit; font-size: 13px; resize: vertical; }
.assist-file { display: grid; gap: 5px; margin-top: 10px; }
.assist-file > span { color: var(--ink); font-size: 13px; font-weight: 700; }
.assist-file small { color: var(--muted); font-size: 12px; }
.assist-source { margin-top: 10px; border: 1px solid var(--line); background: var(--surface, #f7f9fc); }
.assist-source summary { padding: 7px 10px; color: var(--blue); font-size: 12px; font-weight: 700; cursor: pointer; }
.assist-source pre { max-height: 220px; margin: 0; padding: 0 10px 10px; overflow: auto; color: var(--ink); font: inherit; font-size: 12px; white-space: pre-wrap; }
.assist-actions { display: flex; align-items: center; gap: 10px; }
.assist-low { color: var(--warning); font-size: 12px; }

.assist-items { display: grid; gap: 10px; }
.assist-items article { display: grid; gap: 6px; padding: 11px 13px; border: 1px solid var(--line); border-left: 3px solid var(--blue); }
.assist-items article.off { opacity: .45; border-left-color: var(--line); }
.assist-pick { display: flex; align-items: center; gap: 8px; cursor: pointer; }
.assist-pick b { font-size: 14px; }
.assist-desc { margin: 0; color: var(--ink); font-size: 12px; line-height: 1.7; }
.assist-fields { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 2px 12px; margin: 0; }
.assist-fields dt { display: inline; color: var(--muted); font-size: 12px; }
.assist-fields dd { display: inline; margin: 0 0 0 4px; font-size: 12px; }
.assist-warn { margin: 0; padding: 6px 9px; border-left: 3px solid var(--warning-graphic); background: #fdf6ec; color: #7a4a10; font-size: 12px; }
.assist-evidence { margin: 0; color: var(--muted); font-size: 12px; }
.assist-similar { display: grid; gap: 6px; padding: 8px 10px; background: var(--paper); font-size: 12px; }
.assist-case { display: grid; gap: 1px; }
.assist-case em { color: var(--muted); font-style: normal; }
.assist-case small { color: var(--muted); line-height: 1.6; }
.assist-dialog footer { display: flex; align-items: center; gap: 12px; }
.assist-dialog footer small { color: var(--muted); font-size: 12px; }
</style>
